import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, IconButton, Chip,
    Alert, CircularProgress, Paper, MenuItem, Select,
    Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Print as PrintIcon,
    Refresh as RefreshIcon,
    FileCopy as CopyIcon,
    Edit as EditIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    getMikrotiks,
    getAddressLists,
    getAddressListEntries,
    addAddressToList,
    removeAddressFromList,
    deleteAddressList,
    printAddressList
} from '../api/mikrotik.api';

const MikroTikAddressLists = () => {
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState(null);
    const [lists, setLists] = useState({});
    const [selectedList, setSelectedList] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [newAddress, setNewAddress] = useState('');
    const [newComment, setNewComment] = useState('');
    const [editingEntry, setEditingEntry] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadMikrotiks();
    }, []);

    useEffect(() => {
        if (selectedMikrotik) {
            loadAddressLists();
        }
    }, [selectedMikrotik]);

    const loadMikrotiks = async () => {
        try {
            const response = await getMikrotiks({ enabled_only: true });
            setMikrotiks(response.data);
            if (response.data.length > 0) {
                setSelectedMikrotik(response.data[0]);
            }
        } catch (error) {
            setError('Error cargando MikroTiks');
        }
    };

    const loadAddressLists = async () => {
        setLoading(true);
        try {
            const response = await getAddressLists(selectedMikrotik.id);
            setLists(response.data);
        } catch (error) {
            setError('Error cargando address lists');
        }
        setLoading(false);
    };

    const loadListEntries = async (listName) => {
        setLoading(true);
        try {
            const response = await getAddressListEntries(selectedMikrotik.id, listName);
            setEntries(response.data.entries);
            setSelectedList(listName);
        } catch (error) {
            setError('Error cargando entradas de la lista');
        }
        setLoading(false);
    };

    const handleAddAddress = async () => {
        if (!newAddress) return;
        
        try {
            await addAddressToList(selectedMikrotik.id, selectedList, {
                address: newAddress,
                comment: newComment
            });
            setSuccess('Dirección agregada exitosamente');
            setNewAddress('');
            setNewComment('');
            setOpenDialog(false);
            loadListEntries(selectedList);
            loadAddressLists();
        } catch (error) {
            setError('Error agregando dirección');
        }
    };

    const handleEditAddress = async () => {
        if (!editingEntry) return;
        
        try {
            // Eliminar la entrada antigua
            await removeAddressFromList(selectedMikrotik.id, selectedList, editingEntry.oldAddress);
            // Agregar la nueva
            await addAddressToList(selectedMikrotik.id, selectedList, {
                address: editingEntry.newAddress,
                comment: editingEntry.newComment
            });
            setSuccess('Dirección actualizada exitosamente');
            setOpenEditDialog(false);
            setEditingEntry(null);
            loadListEntries(selectedList);
            loadAddressLists();
        } catch (error) {
            setError('Error actualizando dirección');
        }
    };

    const handleRemoveAddress = async (address) => {
        if (!window.confirm(`¿Eliminar dirección ${address} de la lista?`)) return;
        
        try {
            await removeAddressFromList(selectedMikrotik.id, selectedList, address);
            setSuccess('Dirección eliminada');
            loadListEntries(selectedList);
            loadAddressLists();
        } catch (error) {
            setError('Error eliminando dirección');
        }
    };

    const handleDeleteList = async (listName) => {
        if (!window.confirm(`¿Eliminar completamente la lista "${listName}"?`)) return;
        
        try {
            await deleteAddressList(selectedMikrotik.id, listName);
            setSuccess('Lista eliminada');
            if (selectedList === listName) {
                setSelectedList(null);
                setEntries([]);
            }
            loadAddressLists();
        } catch (error) {
            setError('Error eliminando lista');
        }
    };

    const handlePrint = async (listName) => {
        try {
            const response = await printAddressList(selectedMikrotik.id, listName);
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                <head>
                    <title>Address List - ${listName}</title>
                    <style>
                        body { font-family: monospace; margin: 20px; }
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                    </style>
                </head>
                <body>
                    <h1>Address List: ${listName}</h1>
                    <h3>MikroTik: ${response.data.mikrotik} (${response.data.host})</h3>
                    <p>Total: ${response.data.total_entries} direcciones</p>
                    <table>
                        <thead>
                            <tr><th>Dirección IP</th><th>Comentario</th></tr>
                        </thead>
                        <tbody>
                            ${response.data.entries.map(entry => `
                                <tr>
                                    <td>${entry.address}</td>
                                    <td>${entry.comment || '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            setError('Error generando reporte');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSuccess('Copiado al portapapeles');
    };

    const openEditDialogForEntry = (entry) => {
        setEditingEntry({
            oldAddress: entry.address,
            oldComment: entry.comment || '',
            newAddress: entry.address,
            newComment: entry.comment || ''
        });
        setOpenEditDialog(true);
    };

    const filteredEntries = entries.filter(entry => 
        entry.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (entry.comment && entry.comment.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Address Lists</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Select
                        value={selectedMikrotik?.id || ''}
                        onChange={(e) => {
                            const mik = mikrotiks.find(m => m.id === e.target.value);
                            setSelectedMikrotik(mik);
                            setSelectedList(null);
                        }}
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {mikrotiks.map(m => (
                            <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadAddressLists}
                    >
                        Refrescar
                    </Button>
                </Box>
            </Box>

            {selectedMikrotik && (
                <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 200px)' }}>
                    {/* Lista de address lists */}
                    <Paper sx={{ width: 280, overflow: 'auto' }}>
                        <List>
                            {Object.keys(lists).sort().map((listName) => (
                                <ListItem
                                    key={listName}
                                    button
                                    selected={selectedList === listName}
                                    onClick={() => loadListEntries(listName)}
                                    secondaryAction={
                                        <Box>
                                            <Tooltip title="Imprimir">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handlePrint(listName)}
                                                    size="small"
                                                >
                                                    <PrintIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar lista">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleDeleteList(listName)}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight="bold">
                                                {listName}
                                            </Typography>
                                        }
                                        secondary={`${lists[listName].length} direcciones`}
                                    />
                                </ListItem>
                            ))}
                            {Object.keys(lists).length === 0 && !loading && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography color="textSecondary">
                                        No hay address lists
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </Paper>
                    
                    {/* Entradas de la lista seleccionada */}
                    <Paper sx={{ flex: 1, overflow: 'auto' }}>
                        {selectedList && (
                            <>
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="h6">
                                        {selectedList}
                                        <Chip 
                                            label={`${filteredEntries.length} direcciones`} 
                                            size="small" 
                                            sx={{ ml: 2 }}
                                        />
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Buscar..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ width: 200 }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setOpenDialog(true)}
                                            size="small"
                                        >
                                            Agregar
                                        </Button>
                                    </Box>
                                </Box>
                                
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Dirección IP / Rango</TableCell>
                                                    <TableCell>Comentario</TableCell>
                                                    <TableCell>Estado</TableCell>
                                                    <TableCell align="center">Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredEntries.map((entry) => (
                                                    <TableRow key={entry.id}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                                                {entry.address}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {entry.comment || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {entry.disabled && (
                                                                <Chip label="Deshabilitado" size="small" color="warning" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Copiar">
                                                                <IconButton size="small" onClick={() => copyToClipboard(entry.address)}>
                                                                    <CopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Editar">
                                                                <IconButton size="small" onClick={() => openEditDialogForEntry(entry)}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Eliminar">
                                                                <IconButton size="small" color="error" onClick={() => handleRemoveAddress(entry.address)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {filteredEntries.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            <Typography color="textSecondary" sx={{ py: 4 }}>
                                                                {searchTerm ? 'No se encontraron direcciones' : 'No hay direcciones en esta lista'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                        {!selectedList && !loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="textSecondary">
                                    Selecciona una address list para ver sus direcciones
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}
            
            {/* Dialog para agregar dirección */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Agregar dirección a {selectedList}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Dirección IP / Rango / CIDR"
                        fullWidth
                        variant="outlined"
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                        placeholder="Ej: 192.168.1.100, 192.168.1.0/24, 192.168.1.100-192.168.1.200"
                        helperText="Puedes ingresar IP individual, rango o CIDR"
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Comentario"
                        fullWidth
                        variant="outlined"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Ej: Cliente X, Servidor, etc."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleAddAddress} variant="contained">Agregar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para editar dirección */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Editar dirección en {selectedList}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Dirección IP / Rango / CIDR"
                        fullWidth
                        variant="outlined"
                        value={editingEntry?.newAddress || ''}
                        onChange={(e) => setEditingEntry({ ...editingEntry, newAddress: e.target.value })}
                        placeholder="Ej: 192.168.1.100"
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Comentario"
                        fullWidth
                        variant="outlined"
                        value={editingEntry?.newComment || ''}
                        onChange={(e) => setEditingEntry({ ...editingEntry, newComment: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
                    <Button onClick={handleEditAddress} variant="contained">Actualizar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MikroTikAddressLists;
